import { z } from 'zod';

import { PisCofinsCreditType, type VendorKnowledgeReviewStatus } from '../generated/enums.js';
import { RECON_V2_ADJUSTMENT_TYPES } from '../reconciliation/decision-queue-v2.js';
import type { WorkflowOriginResponse } from '../wf-origin.js';
import {
  AI_SKIPPED_REFERENCE_PATTERNS,
  AI_SPLIT_DIMENSIONS,
  AiCreditFlagsNoteCode,
  AiSaveRefusalCode,
  AiSkippedReferenceCode,
  type AiSkippedReferencePattern,
  type AiSplitDimension,
  AiSplitRejectionCode,
} from './classification-split-allocation.js';

/**
 * Vendor knowledge (ALC-4551, ADR 0033, docs/features/vendor-knowledge/decision-record.md).
 *
 * What an entity has learned about a vendor, kept in ONE structure: a row per (tenant, entity,
 * vendor, kind, the kind's key dimensions), versioned by effective competence. The kinds live in a
 * catalog in code (`@erp/services/ap` vendor-knowledge catalog); each kind declares its payload
 * schema, key dimensions, where it applies and which offer proposes it. This file holds the pieces
 * both sides share: the kind ids, the payload schemas, the dimension shapes and the coded errors.
 */

// ---------------------------------------------------------------------------
// Kinds, dimensions, where a kind applies
// ---------------------------------------------------------------------------

/** The v1 kinds (D3). A new kind is a catalog entry plus an id here, never a migration. */
export const VendorKnowledgeKind = {
  WITHHOLDING: 'withholding',
  PIS_COFINS_CREDIT: 'pis_cofins_credit',
  INSTRUCTION: 'instruction',
  /** ALC-4663: how this vendor's bills meet their card or bank movement (reconciliation agent D7). */
  RECONCILIATION_RULE: 'reconciliation_rule',
} as const;
export type VendorKnowledgeKind = (typeof VendorKnowledgeKind)[keyof typeof VendorKnowledgeKind];

export const VENDOR_KNOWLEDGE_KIND_VALUES = [
  VendorKnowledgeKind.WITHHOLDING,
  VendorKnowledgeKind.PIS_COFINS_CREDIT,
  VendorKnowledgeKind.INSTRUCTION,
  VendorKnowledgeKind.RECONCILIATION_RULE,
] as const satisfies readonly VendorKnowledgeKind[];

/** Names of the dimensions a kind can be keyed by. Withholding and credit: service type (D8). */
export const VendorKnowledgeDimension = {
  SERVICE_TYPE: 'serviceTypeId',
  PURPOSE: 'purpose',
  /** Reconciliation rule: where the money moved, `card` or `bank`. */
  MOVEMENT_SOURCE: 'movementSource',
  /** Reconciliation rule: ISO 4217 currency of the bill. */
  CURRENCY: 'currency',
} as const;
export type VendorKnowledgeDimension =
  (typeof VendorKnowledgeDimension)[keyof typeof VendorKnowledgeDimension];

/** Where a kind is consumed. Applying a kind is the job of later slices; this only labels it. */
export const VendorKnowledgeAppliesAt = {
  BILL_WITHHOLDING: 'bill_withholding',
  BILL_PIS_COFINS_CREDIT: 'bill_pis_cofins_credit',
  AI_CONTEXT: 'ai_context',
  RECONCILIATION: 'reconciliation',
} as const;
export type VendorKnowledgeAppliesAt =
  (typeof VendorKnowledgeAppliesAt)[keyof typeof VendorKnowledgeAppliesAt];

/**
 * Offers that can propose knowledge (D24). Dismissals are recorded per offer kind. The first four
 * share the id of the kind they propose (`reconciliation_rule` is the reconciliation agent's rule
 * proposal). The two bill offers (D22, ALC-4561) come from a trigger on the bill and propose the
 * withholding and PIS/COFINS credit kinds together, keyed by the bill's service type: "new
 * service" when the entity has no rule for the vendor's service yet, "remember" after a person
 * set withholdings or credits by hand.
 */
export const VendorKnowledgeOfferKind = {
  WITHHOLDING: 'withholding',
  PIS_COFINS_CREDIT: 'pis_cofins_credit',
  INSTRUCTION: 'instruction',
  RECONCILIATION_RULE: 'reconciliation_rule',
  NEW_SERVICE: 'new_service',
  REMEMBER: 'remember',
  /**
   * ALC-4565 (W2-3): the approval-rule offer on the bill page ("Enviar para aprovação" /
   * "Aprovar"). Keyed per (entity, vendor) only: `dimensionKey` is '' and the service
   * type is null. Not a bill knowledge offer: it proposes workflows, not knowledge rows.
   */
  APPROVAL_AUTOMATION: 'approval_automation',
} as const;
export type VendorKnowledgeOfferKind =
  (typeof VendorKnowledgeOfferKind)[keyof typeof VendorKnowledgeOfferKind];

export const VENDOR_KNOWLEDGE_OFFER_KIND_VALUES = [
  VendorKnowledgeOfferKind.WITHHOLDING,
  VendorKnowledgeOfferKind.PIS_COFINS_CREDIT,
  VendorKnowledgeOfferKind.INSTRUCTION,
  VendorKnowledgeOfferKind.RECONCILIATION_RULE,
  VendorKnowledgeOfferKind.NEW_SERVICE,
  VendorKnowledgeOfferKind.REMEMBER,
  VendorKnowledgeOfferKind.APPROVAL_AUTOMATION,
] as const satisfies readonly VendorKnowledgeOfferKind[];

/** The offers a bill makes (D22). */
export const BILL_KNOWLEDGE_OFFER_KINDS = [
  VendorKnowledgeOfferKind.NEW_SERVICE,
  VendorKnowledgeOfferKind.REMEMBER,
] as const satisfies readonly VendorKnowledgeOfferKind[];
export type BillKnowledgeOfferKind = (typeof BILL_KNOWLEDGE_OFFER_KINDS)[number];

/** Dimension values of a row, by dimension name. Only the dimensions the kind declares are set. */
export interface VendorKnowledgeDimensions {
  serviceTypeId?: string;
  purpose?: VendorKnowledgeInstructionPurpose;
  movementSource?: VendorReconciliationMovementSource;
  currency?: string;
}

// ---------------------------------------------------------------------------
// Payload schemas (one per kind)
// ---------------------------------------------------------------------------

/** Taxes a vendor's withholding knowledge can speak to (D7). */
export const VENDOR_WITHHOLDING_TAXES = ['IRRF', 'PIS', 'COFINS', 'CSLL', 'INSS', 'ISS'] as const;
export type VendorWithholdingTax = (typeof VENDOR_WITHHOLDING_TAXES)[number];

/**
 * One tax: withheld or not, with an optional rate (percent, 0 < rate <= 100). Base, minimum and
 * accumulation come from the system rules, never from the knowledge (D7). A rate only makes sense
 * when the tax applies.
 */
export const VendorWithholdingTaxRuleSchema = z
  .object({
    applies: z.boolean(),
    rate: z.number().gt(0).lte(100).optional(),
  })
  .strict()
  .refine((value) => value.applies || value.rate === undefined, {
    message: 'rate is only allowed when the tax applies',
    path: ['rate'],
  });
export type VendorWithholdingTaxRule = z.infer<typeof VendorWithholdingTaxRuleSchema>;

const withholdingTaxShape = Object.fromEntries(
  VENDOR_WITHHOLDING_TAXES.map((tax) => [tax, VendorWithholdingTaxRuleSchema.optional()]),
) as Record<VendorWithholdingTax, z.ZodOptional<typeof VendorWithholdingTaxRuleSchema>>;

export const VendorWithholdingPayloadSchema = z
  .object({
    taxes: z
      .object(withholdingTaxShape)
      .strict()
      .refine((taxes) => Object.values(taxes).some((rule) => rule !== undefined), {
        message: 'at least one tax is required',
      }),
  })
  .strict();
export type VendorWithholdingPayload = z.infer<typeof VendorWithholdingPayloadSchema>;

/** What the vendor rule did to one tax of a bill's calculation (ALC-4556). */
export const VendorRuleTaxEffect = {
  /** The vendor rule says the tax is not withheld: the system rule was removed. */
  REMOVED: 'REMOVED',
  /** The vendor rule rate replaced the system rate. */
  RATE_OVERRIDDEN: 'RATE_OVERRIDDEN',
  /** The vendor rule rate created a rule the system does not have (total value, no minimum). */
  RULE_ADDED: 'RULE_ADDED',
  /** The vendor rule confirms the tax without a rate: the system rule stays as is. */
  KEPT_SYSTEM: 'KEPT_SYSTEM',
  /** The vendor rule asks for the tax but there is no rate anywhere to compute it. */
  NO_RATE_AVAILABLE: 'NO_RATE_AVAILABLE',
  /** ISS is evidenced by the document only; the vendor rule is recorded but not computed. */
  NOT_COMPUTED: 'NOT_COMPUTED',
} as const;
export type VendorRuleTaxEffect = (typeof VendorRuleTaxEffect)[keyof typeof VendorRuleTaxEffect];

export interface VendorRuleTaxDecision {
  taxType: VendorWithholdingTax;
  applies: boolean;
  /** Percent (1 means 1%); null when the rule gives no rate. */
  ratePercent: number | null;
  effect: VendorRuleTaxEffect;
}

/** A vendor status whose withholding exemption a vendor rule can override (D7). */
export type VendorRuleExemptionStatus = 'SIMPLES' | 'MEI';

/**
 * What a bill records about the vendor withholding rule it was calculated with (D6, D28), under
 * `withholdingCalculationDetails.vendorRule`. Read by the bill page and by the divergence check.
 */
export interface VendorRuleSnapshot {
  kind: typeof VendorKnowledgeKind.WITHHOLDING;
  knowledgeId: string;
  effectiveFrom: string;
  /** `YYYY-MM` the version was chosen for. */
  competence: string;
  entityId: string;
  vendorId: string;
  serviceTypeId: string;
  /** The payload exactly as it was when applied. */
  payload: VendorWithholdingPayload;
  precedence: readonly string[];
  /** Per tax: what the vendor rule decided. */
  decisions: VendorRuleTaxDecision[];
  /** Per tax: the rate (percent) and base used and the amount that came out. */
  values: {
    taxType: string;
    ratePercent: number;
    calculationBase: string;
    minimumAmount: number | null;
    amount: number;
  }[];
  /**
   * The vendor was a Simples Nacional or MEI optant when the rule was applied (the vendor registry
   * or the regime on the document). Such a vendor is exempt from IRRF, PIS, COFINS and CSLL unless
   * the rule gives the tax an explicit rate. Recorded so the withholding profile (ALC-4563) can tell
   * a system rate that was not withheld from one that was. Absent on snapshots written before
   * ALC-4563: the profile then cannot tell, and the bill has none.
   */
  vendorIsSimplesOrMei?: boolean;
  /** Where the values were applied from. */
  origin: 'INTAKE' | 'BILL_UPDATE' | 'LINK_EXISTING' | 'APPLY_TO_DRAFTS';
  appliedAt: string;
  /**
   * Whether the bill retained withholdings before the rule was applied. False when the rule
   * switched retention on, so the bill can switch it back off once the rule no longer applies.
   * Absent on snapshots written before it was recorded.
   */
  retainedBeforeRule?: boolean;
  /**
   * What a bill whose retention was off carried before the rule switched retention on: its
   * non-zero tax amounts, which were informational and never deducted, and its total. The rule
   * retains only the taxes it manages and zeroes these; once the rule no longer applies they are
   * put back exactly. Absent when retention was already on.
   */
  informationalBeforeRule?: {
    amounts: Partial<Record<VendorWithholdingTax, number>>;
    totalWithholdingAmount: number | null;
  };
  /**
   * The taxes the intake document stated, as opposed to the ones a calculation filled. Set when a
   * rule first reaches the bill and carried forward like `retainedBeforeRule`, so a later rule that
   * removes one of them sends the bill to review whatever origin the latest recalculation had.
   * Absent on snapshots written before it was recorded.
   */
  statedTaxTypes?: VendorWithholdingTax[];
  /**
   * The vendor's Simples Nacional or MEI status (`ApVendor`) whose exemption the rule overrode
   * (D7): the rule applies a tax the status exempts, so the bill withholds it and warns. Absent
   * when the rule overrides no exemption.
   */
  vendorRuleOverridesExemption?: VendorRuleExemptionStatus;
  /** The exempt taxes the rule applied anyway, with `vendorRuleOverridesExemption`. */
  exemptionOverriddenTaxTypes?: VendorWithholdingTax[];
}

/** How the revenue the purchase supports is taxed for PIS/COFINS (D9). */
export const VendorCreditRevenueUse = {
  CUMULATIVE: 'CUMULATIVE',
  NON_CUMULATIVE: 'NON_CUMULATIVE',
  COMMON: 'COMMON',
} as const;
export type VendorCreditRevenueUse =
  (typeof VendorCreditRevenueUse)[keyof typeof VendorCreditRevenueUse];

/**
 * Evidence a vendor rule leaves on the revenue use it filled. A classification a person makes for a
 * different revenue use never carries this code: the bill form clears it and the API refuses it.
 * Setting the revenue use back to the value the rule wrote restores this evidence.
 */
export const VENDOR_RULE_REVENUE_USE_EVIDENCE_CODE = 'VENDOR_RULE';

/** Description stored with {@link VENDOR_RULE_REVENUE_USE_EVIDENCE_CODE}. */
export const VENDOR_RULE_REVENUE_USE_EVIDENCE_DESCRIPTION =
  'Uso da receita definido pela regra de crédito PIS/COFINS do fornecedor.';

/**
 * The credit types a vendor rule can set. Only the two the bill accepts from a person
 * (`UpdateBillDto.creditType`) and computes with the basic PIS/COFINS rates: PRESUMED and IMPORT are
 * "specific treatment" credits (locked on the bill form), so a rule that wrote them would leave a
 * bill the manual DTO rejects on save and amounts at the wrong rates.
 */
export const VENDOR_CREDIT_TYPES = [
  PisCofinsCreditType.BASIC,
  PisCofinsCreditType.FIXED_ASSET,
] as const;
export type VendorCreditType = (typeof VENDOR_CREDIT_TYPES)[number];

const PIS_COFINS_CREDIT_TYPE_VALUES = VENDOR_CREDIT_TYPES;
const VENDOR_CREDIT_REVENUE_USE_VALUES = Object.values(VendorCreditRevenueUse) as [
  VendorCreditRevenueUse,
  ...VendorCreditRevenueUse[],
];

/** PIS/COFINS credit only: whether the purchase generates it, its type and the revenue use (D9). */
export const VendorPisCofinsCreditPayloadSchema = z
  .object({
    generatesCredit: z.boolean(),
    creditType: z.enum(PIS_COFINS_CREDIT_TYPE_VALUES).optional(),
    revenueUse: z.enum(VENDOR_CREDIT_REVENUE_USE_VALUES).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.generatesCredit || (value.creditType === undefined && value.revenueUse === undefined),
    {
      message: 'creditType and revenueUse are only allowed when the purchase generates credit',
      path: ['generatesCredit'],
    },
  )
  .refine(
    (value) =>
      !value.generatesCredit || (value.creditType !== undefined && value.revenueUse !== undefined),
    {
      message: 'creditType and revenueUse are required when the purchase generates credit',
      path: ['generatesCredit'],
    },
  )
  // A purchase used only against cumulative revenue gives no PIS/COFINS credit (Lei 10.833/2003,
  // art. 3, par. 7; Lei 10.637/2002, art. 3, par. 7): such a vendor's rule says it generates none.
  .refine(
    (value) => !(value.generatesCredit && value.revenueUse === VendorCreditRevenueUse.CUMULATIVE),
    {
      message:
        'a purchase used only against cumulative revenue generates no credit; set generatesCredit to false',
      path: ['revenueUse'],
    },
  );
export type VendorPisCofinsCreditPayload = z.infer<typeof VendorPisCofinsCreditPayloadSchema>;

/**
 * PIS/COFINS credits end with the 12/2026 competence (tax reform): no credit rule applies after
 * it, and a credit rule that would start after it is refused. `YYYY-MM` compares as a string.
 */
export const LAST_PIS_COFINS_CREDIT_COMPETENCE = '2026-12';

/** What an instruction is for (D16). One row per (vendor, purpose) in v1. */
export const VendorKnowledgeInstructionPurpose = {
  CLASSIFICATION_ALLOCATION: 'classification_allocation',
  DOCUMENT_LINKING: 'document_linking',
} as const;
export type VendorKnowledgeInstructionPurpose =
  (typeof VendorKnowledgeInstructionPurpose)[keyof typeof VendorKnowledgeInstructionPurpose];

export const VENDOR_KNOWLEDGE_INSTRUCTION_PURPOSE_VALUES = [
  VendorKnowledgeInstructionPurpose.CLASSIFICATION_ALLOCATION,
  VendorKnowledgeInstructionPurpose.DOCUMENT_LINKING,
] as const satisfies readonly VendorKnowledgeInstructionPurpose[];

export const VENDOR_KNOWLEDGE_INSTRUCTION_MAX_LENGTH = 1000;

export const VendorInstructionPayloadSchema = z
  .object({
    text: z.string().trim().min(1).max(VENDOR_KNOWLEDGE_INSTRUCTION_MAX_LENGTH),
  })
  .strict();
export type VendorInstructionPayload = z.infer<typeof VendorInstructionPayloadSchema>;

/** Where the money moved for a reconciliation rule (D7). */
export const VendorReconciliationMovementSource = {
  CARD: 'card',
  BANK: 'bank',
} as const;
export type VendorReconciliationMovementSource =
  (typeof VendorReconciliationMovementSource)[keyof typeof VendorReconciliationMovementSource];

export const VENDOR_RECONCILIATION_MOVEMENT_SOURCE_VALUES = [
  VendorReconciliationMovementSource.CARD,
  VendorReconciliationMovementSource.BANK,
] as const satisfies readonly VendorReconciliationMovementSource[];

/**
 * Vendor reconciliation rule (ALC-4663, reconciliation agent D7): the movement is the bill amount
 * plus `gapRatePercent` percent (a card charge that is the receipt x 1.04 has 4), and the gap is
 * booked as `bookAs` (a settlement adjustment type, for example `IOF`). Keyed by movement source
 * and bill currency (dimensions), per entity and vendor.
 */
export const VendorReconciliationRulePayloadSchema = z
  .object({
    gapRatePercent: z.number().gt(0).lte(100),
    bookAs: z.enum(RECON_V2_ADJUSTMENT_TYPES),
  })
  .strict();
export type VendorReconciliationRulePayload = z.infer<typeof VendorReconciliationRulePayloadSchema>;

/**
 * Most characters of instruction text that reach one AI prompt (ALC-4554). Each instruction is
 * already at most {@link VENDOR_KNOWLEDGE_INSTRUCTION_MAX_LENGTH}; this bounds the sum so a vendor
 * with several long instructions cannot crowd the document and the dimensions out of the prompt.
 */
export const VENDOR_INSTRUCTIONS_PROMPT_CHAR_CAP = 2000;

/**
 * An active instruction as an AI prompt receives it (ALC-4554, D15-D17). Entity-scoped: it carries
 * the entity and the vendor it was loaded for, so a bundle spanning vendors keeps them apart.
 */
export interface VendorInstructionForPrompt {
  knowledgeId: string;
  entityId: string;
  vendorId: string;
  purpose: VendorKnowledgeInstructionPurpose;
  text: string;
}

/**
 * Keeps the instructions that fit in `cap` characters, in the order given. The one that overflows
 * is cut with an ellipsis, the rest are dropped. What the prompt receives is exactly what the
 * returned list says, so the ids recorded on a suggestion are the ids the model could see.
 */
export function capVendorInstructionsForPrompt<T extends { text: string }>(
  instructions: readonly T[],
  cap: number = VENDOR_INSTRUCTIONS_PROMPT_CHAR_CAP,
): T[] {
  const kept: T[] = [];
  let remaining = cap;
  for (const instruction of instructions) {
    if (remaining <= 0) break;
    const text = instruction.text.trim();
    if (text.length === 0) continue;
    if (text.length <= remaining) {
      kept.push({ ...instruction, text });
      remaining -= text.length;
      continue;
    }
    if (remaining > 1) kept.push({ ...instruction, text: `${text.slice(0, remaining - 1)}…` });
    break;
  }
  return kept;
}

/**
 * What a bill remembers about the instructions its AI suggestion used (ALC-4554). A snapshot of
 * the text, because a new version of an instruction gets a new id and the bill must keep showing
 * what the AI was actually told. `contradictionNote` is the AI's own note when an instruction
 * disagreed with a value a rule or workflow had already set; nothing was overwritten.
 * `text` is always stored; the bill read leaves it out for a caller without `party:read`, the
 * permission that reads vendor knowledge (see `withoutBillAiInstructionText`).
 */
export interface BillAiInstructionUse {
  instructions: {
    knowledgeId: string;
    purpose: VendorKnowledgeInstructionPurpose;
    text?: string;
  }[];
  contradictionNote: string | null;
  recordedAt: string;
  /**
   * The split (rateio) the AI suggested for a dimension because an instruction asked for it
   * (ALC-4641): only splits that were actually saved. Labels are a snapshot of what the AI was
   * shown, so the bill keeps reading the same after a cost center is renamed.
   */
  splits?: BillAiInstructionSplit[];
  /** Dimensions whose split the AI proposed and the system refused, by stable code. */
  splitRejections?: BillAiInstructionSplitRejection[];
  /**
   * The AI's whole classification was refused by the validator, by stable code (ALC-4641). Nothing
   * of the suggestion was saved, so a record that carries this one holds no instruction or split.
   */
  saveRefusal?: BillAiInstructionSaveRefusal;
  /**
   * The vendor's last bill classifies per line item or with a custom breakdown, so its history
   * was not copied and the instruction decided on its own (ALC-4641). Names the reference bill.
   */
  skippedReference?: BillAiInstructionSkippedReference;
  /**
   * The PIS/COFINS credit flags of the vendor's last bill were not carried over, or the copy
   * failed (ALC-4641). Names the reference bill.
   */
  creditFlags?: BillAiInstructionCreditFlags;
}

export interface BillAiInstructionCreditFlags {
  code: AiCreditFlagsNoteCode;
  referenceBillId: string;
}

export interface BillAiInstructionSaveRefusal {
  code: AiSaveRefusalCode;
}

export interface BillAiInstructionSkippedReference {
  code: AiSkippedReferenceCode;
  referenceBillId: string;
  pattern: AiSkippedReferencePattern;
}

export interface BillAiInstructionSplitTarget {
  id: string;
  /** Null for a dimension with no code (departments). */
  code: string | null;
  name: string;
  percent: number;
}

export interface BillAiInstructionSplit {
  dimension: AiSplitDimension;
  targets: BillAiInstructionSplitTarget[];
}

export interface BillAiInstructionSplitRejection {
  dimension: AiSplitDimension;
  code: AiSplitRejectionCode;
}

const AI_SPLIT_DIMENSION_SET: ReadonlySet<string> = new Set(AI_SPLIT_DIMENSIONS);
const AI_SPLIT_REJECTION_CODE_SET: ReadonlySet<string> = new Set(
  Object.values(AiSplitRejectionCode),
);

function readSplitTargets(value: unknown): BillAiInstructionSplitTarget[] {
  if (!Array.isArray(value)) return [];
  const targets: BillAiInstructionSplitTarget[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.id !== 'string' || typeof item.name !== 'string') continue;
    if (typeof item.percent !== 'number' || !Number.isFinite(item.percent)) continue;
    targets.push({
      id: item.id,
      code: typeof item.code === 'string' ? item.code : null,
      name: item.name,
      percent: item.percent,
    });
  }
  return targets;
}

function readSplits(value: unknown): BillAiInstructionSplit[] {
  if (!Array.isArray(value)) return [];
  const splits: BillAiInstructionSplit[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.dimension !== 'string' || !AI_SPLIT_DIMENSION_SET.has(item.dimension)) continue;
    const targets = readSplitTargets(item.targets);
    if (targets.length === 0) continue;
    splits.push({ dimension: item.dimension as AiSplitDimension, targets });
  }
  return splits;
}

const AI_SAVE_REFUSAL_CODE_SET: ReadonlySet<string> = new Set(Object.values(AiSaveRefusalCode));

function readSaveRefusal(value: unknown): BillAiInstructionSaveRefusal | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { code } = value as Record<string, unknown>;
  if (typeof code !== 'string' || !AI_SAVE_REFUSAL_CODE_SET.has(code)) return null;
  return { code: code as AiSaveRefusalCode };
}

const AI_SKIPPED_REFERENCE_CODE_SET: ReadonlySet<string> = new Set(
  Object.values(AiSkippedReferenceCode),
);
const AI_SKIPPED_REFERENCE_PATTERN_SET: ReadonlySet<string> = new Set(
  AI_SKIPPED_REFERENCE_PATTERNS,
);

function readSkippedReference(value: unknown): BillAiInstructionSkippedReference | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { code, referenceBillId, pattern } = value as Record<string, unknown>;
  if (typeof code !== 'string' || !AI_SKIPPED_REFERENCE_CODE_SET.has(code)) return null;
  if (typeof referenceBillId !== 'string' || referenceBillId.length === 0) return null;
  if (typeof pattern !== 'string' || !AI_SKIPPED_REFERENCE_PATTERN_SET.has(pattern)) return null;
  return {
    code: code as AiSkippedReferenceCode,
    referenceBillId,
    pattern: pattern as AiSkippedReferencePattern,
  };
}

const AI_CREDIT_FLAGS_NOTE_CODE_SET: ReadonlySet<string> = new Set(
  Object.values(AiCreditFlagsNoteCode),
);

function readCreditFlags(value: unknown): BillAiInstructionCreditFlags | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { code, referenceBillId } = value as Record<string, unknown>;
  if (typeof code !== 'string' || !AI_CREDIT_FLAGS_NOTE_CODE_SET.has(code)) return null;
  if (typeof referenceBillId !== 'string' || referenceBillId.length === 0) return null;
  return { code: code as AiCreditFlagsNoteCode, referenceBillId };
}

function readSplitRejections(value: unknown): BillAiInstructionSplitRejection[] {
  if (!Array.isArray(value)) return [];
  const rejections: BillAiInstructionSplitRejection[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.dimension !== 'string' || !AI_SPLIT_DIMENSION_SET.has(item.dimension)) continue;
    if (typeof item.code !== 'string' || !AI_SPLIT_REJECTION_CODE_SET.has(item.code)) continue;
    rejections.push({
      dimension: item.dimension as AiSplitDimension,
      code: item.code as AiSplitRejectionCode,
    });
  }
  return rejections;
}

/**
 * Tolerant reader for the `ApBill.aiInstructionUse` JSON column: returns null for anything that
 * is not a snapshot with at least one instruction or a contradiction note, so the bill UI never
 * renders an empty panel. A note with no instruction id is kept (the model may report a
 * contradiction without naming the instruction).
 */
export function readBillAiInstructionUse(value: unknown): BillAiInstructionUse | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.instructions)) return null;
  const instructions: BillAiInstructionUse['instructions'] = [];
  const seen = new Set<string>();
  for (const entry of record.instructions) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.knowledgeId !== 'string') continue;
    if (item.text !== undefined && typeof item.text !== 'string') continue;
    if (seen.has(item.knowledgeId)) continue;
    seen.add(item.knowledgeId);
    instructions.push({
      knowledgeId: item.knowledgeId,
      purpose:
        item.purpose === 'document_linking' ? 'document_linking' : 'classification_allocation',
      ...(typeof item.text === 'string' ? { text: item.text } : {}),
    });
  }
  const note =
    typeof record.contradictionNote === 'string' && record.contradictionNote.trim().length > 0
      ? record.contradictionNote.trim()
      : null;
  // A note without a reported instruction still counts: it is the user-visible signal that a rule
  // won over an instruction, so the bill keeps showing it.
  // A suggested split, or a refused one, is just as visible: the bill shows it next to the AI's
  // suggestion even when the model named no instruction.
  const splits = readSplits(record.splits);
  const splitRejections = readSplitRejections(record.splitRejections);
  const saveRefusal = readSaveRefusal(record.saveRefusal);
  const skippedReference = readSkippedReference(record.skippedReference);
  const creditFlags = readCreditFlags(record.creditFlags);
  if (
    instructions.length === 0 &&
    !note &&
    splits.length === 0 &&
    splitRejections.length === 0 &&
    !saveRefusal &&
    !skippedReference &&
    !creditFlags
  ) {
    return null;
  }
  return {
    instructions,
    contradictionNote: note,
    recordedAt: typeof record.recordedAt === 'string' ? record.recordedAt : '',
    ...(splits.length > 0 ? { splits } : {}),
    ...(splitRejections.length > 0 ? { splitRejections } : {}),
    ...(saveRefusal ? { saveRefusal } : {}),
    ...(skippedReference ? { skippedReference } : {}),
    ...(creditFlags ? { creditFlags } : {}),
  };
}

/**
 * The same record without the instruction text, for a reader who may see the bill but not the
 * vendor's knowledge: ids, purposes and the date stay, so the bill still says that instructions
 * were used. The contradiction note goes too, because the AI writes it about what the instruction
 * says. The split, its refusals, the save refusal, the skipped reference and the credit-flag note
 * (ALC-4641) stay: they are the bill's own classification and stable codes, not instruction text.
 * `null` when nothing is left to show.
 */
export function withoutBillAiInstructionText(
  use: BillAiInstructionUse,
): BillAiInstructionUse | null {
  const { splits, splitRejections, saveRefusal, skippedReference, creditFlags } = use;
  const kept = {
    ...(splits && splits.length > 0 ? { splits } : {}),
    ...(splitRejections && splitRejections.length > 0 ? { splitRejections } : {}),
    ...(saveRefusal ? { saveRefusal } : {}),
    ...(skippedReference ? { skippedReference } : {}),
    ...(creditFlags ? { creditFlags } : {}),
  };
  if (use.instructions.length === 0 && Object.keys(kept).length === 0) return null;
  return {
    instructions: use.instructions.map(({ knowledgeId, purpose }) => ({ knowledgeId, purpose })),
    contradictionNote: null,
    recordedAt: use.recordedAt,
    ...kept,
  };
}

/**
 * Vendor instruction text is vendor knowledge. A missing permission list is not permission to
 * read it. `trustedInternal` is only for a result that stays on the server.
 */
export function callerMayReadBillAiInstructionText(
  options: { permissions?: readonly string[]; trustedInternal?: boolean } = {},
): boolean {
  return (
    options.trustedInternal === true ||
    (options.permissions !== undefined &&
      (options.permissions.includes('*') || options.permissions.includes('party:read')))
  );
}

/** The snapshot a reader may see: full text with `party:read`, ids and date otherwise. */
export function billAiInstructionUseForReader(
  value: unknown,
  options: { permissions?: readonly string[]; trustedInternal?: boolean } = {},
): BillAiInstructionUse | null {
  const stored = readBillAiInstructionUse(value);
  if (!stored) return null;
  return callerMayReadBillAiInstructionText(options)
    ? stored
    : withoutBillAiInstructionText(stored);
}

/** Where a vendor rule was applied to a bill from. */
export type BillVendorRuleOrigin = 'INTAKE' | 'BILL_UPDATE' | 'LINK_EXISTING' | 'APPLY_TO_DRAFTS';

/** The bill fields a PIS/COFINS credit rule can write (ALC-4558). */
export const BILL_VENDOR_CREDIT_RULE_FIELDS = [
  'generatesPisCredit',
  'generatesCofinsCredit',
  'creditType',
  'pisCofinsCreditRevenueUse',
] as const;
export type BillVendorCreditRuleField = (typeof BILL_VENDOR_CREDIT_RULE_FIELDS)[number];

/**
 * What a bill remembers about the PIS/COFINS credit rule that filled it (ALC-4558, D9). The values
 * are the ones the rule WROTE, kept so a later edit can tell a value the rule set (and may update)
 * from one a person changed (and must keep). `written` lists the bill fields the rule wrote; a
 * field a person had already set is not in it. Amounts are derived from the base with the shared
 * credit computation, never from rates stored here: the rates are recorded for the record only.
 */
export interface BillVendorCreditRule {
  kind: typeof VendorKnowledgeKind.PIS_COFINS_CREDIT;
  knowledgeId: string;
  effectiveFrom: string;
  /** `YYYY-MM` the version was chosen for. */
  competence: string;
  entityId: string;
  vendorId: string;
  serviceTypeId: string;
  /** The payload exactly as it was when applied. */
  payload: VendorPisCofinsCreditPayload;
  values: {
    generatesCredit: boolean;
    creditType: PisCofinsCreditType | null;
    revenueUse: 'DIRECT_CUMULATIVE' | 'DIRECT_NON_CUMULATIVE' | 'COMMON' | null;
    pisCreditAmount: number;
    cofinsCreditAmount: number;
    calculationBase: number;
    pisRatePercent: number;
    cofinsRatePercent: number;
  };
  written: BillVendorCreditRuleField[];
  origin: BillVendorRuleOrigin;
  appliedAt: string;
}

/**
 * Tolerant reader for the `ApBill.vendorCreditRule` JSON column: null for anything that is not a
 * snapshot of a PIS/COFINS credit rule, so the bill UI and the edit path never trust a bad shape.
 */
export function readBillVendorCreditRule(value: unknown): BillVendorCreditRule | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record.kind !== VendorKnowledgeKind.PIS_COFINS_CREDIT) return null;
  if (typeof record.knowledgeId !== 'string' || typeof record.serviceTypeId !== 'string') {
    return null;
  }
  const values = record.values;
  if (!values || typeof values !== 'object' || Array.isArray(values)) return null;
  if (typeof (values as Record<string, unknown>).generatesCredit !== 'boolean') return null;
  const written = Array.isArray(record.written)
    ? record.written.filter((field): field is BillVendorCreditRuleField =>
        (BILL_VENDOR_CREDIT_RULE_FIELDS as readonly unknown[]).includes(field),
      )
    : [];
  return { ...(record as unknown as BillVendorCreditRule), written };
}

// ---------------------------------------------------------------------------
// Vendor rule divergence (ALC-4559, D10, D11, D28)
// ---------------------------------------------------------------------------

/**
 * Readiness code while a bill holds an open vendor rule divergence. The bill cannot be submitted
 * for approval, by a person, the auto-submit setting or a workflow, until a person resolves it.
 */
export const VENDOR_RULE_DIVERGENCE_OPEN_CODE = 'AP_BILL_VENDOR_RULE_DIVERGENCE_OPEN';

/** The three ways a person resolves a divergence (D11). */
export const VendorRuleDivergenceResolution = {
  /** Keep the document's values, for this bill only. */
  KEEP_DOCUMENT: 'KEEP_DOCUMENT',
  /** Write the rule's values on the bill. */
  APPLY_RULE: 'APPLY_RULE',
  /** Keep the document's values and start a new rule version from the bill's competence. */
  NEW_RULE_VERSION: 'NEW_RULE_VERSION',
} as const;
export type VendorRuleDivergenceResolution =
  (typeof VendorRuleDivergenceResolution)[keyof typeof VendorRuleDivergenceResolution];

export const VENDOR_RULE_DIVERGENCE_RESOLUTION_VALUES = [
  VendorRuleDivergenceResolution.KEEP_DOCUMENT,
  VendorRuleDivergenceResolution.APPLY_RULE,
  VendorRuleDivergenceResolution.NEW_RULE_VERSION,
] as const satisfies readonly VendorRuleDivergenceResolution[];

/**
 * `OPEN` blocks submission. `RESOLVED` records the person's choice. `CLEARED` means the bill no
 * longer differs from the rule without a resolution (a person edited the field, or a key change
 * picked a rule the document agrees with).
 */
export const VendorRuleDivergenceStatus = {
  OPEN: 'OPEN',
  RESOLVED: 'RESOLVED',
  CLEARED: 'CLEARED',
} as const;
export type VendorRuleDivergenceStatus =
  (typeof VendorRuleDivergenceStatus)[keyof typeof VendorRuleDivergenceStatus];

/** Coded refusals of the resolve endpoint. */
export const VendorRuleDivergenceErrorCode = {
  /** The bill has no open divergence: it was already resolved, or there never was one. */
  NOT_OPEN: 'AP_BILL_VENDOR_RULE_DIVERGENCE_NOT_OPEN',
  /** The divergence cleared on its own: a person changed the values, or they now agree. */
  CLEARED: 'AP_BILL_VENDOR_RULE_DIVERGENCE_CLEARED',
  /** The divergence changed since the screen loaded it (another key, another rule version). */
  STALE: 'AP_BILL_VENDOR_RULE_DIVERGENCE_STALE',
  /** Option 3 needs `ap:vendor-knowledge:manage`. */
  NEW_VERSION_FORBIDDEN: 'AP_BILL_VENDOR_RULE_DIVERGENCE_NEW_VERSION_FORBIDDEN',
  /** The rule already has a version starting in the bill's competence; edit it on the vendor page. */
  VERSION_EXISTS: 'AP_BILL_VENDOR_RULE_DIVERGENCE_VERSION_EXISTS',
  /** The bill is past approval: its values can no longer change. */
  BILL_NOT_EDITABLE: 'AP_BILL_VENDOR_RULE_DIVERGENCE_BILL_NOT_EDITABLE',
  /**
   * Option 3 on a bill whose PIS and COFINS credit flags differ: a credit rule holds one
   * "generates credit" answer for both, so it cannot keep the document's values.
   */
  MIXED_CREDIT_FLAGS: 'AP_BILL_VENDOR_RULE_DIVERGENCE_MIXED_CREDIT_FLAGS',
  /**
   * Option 3 on a withholding whose rate, rounded to two decimals and under the IRRF minimum and
   * the PIS/COFINS/CSLL waiver, does not give back the document's amount on this bill.
   */
  RATE_MISMATCH: 'AP_BILL_VENDOR_RULE_DIVERGENCE_RATE_MISMATCH',
} as const;
export type VendorRuleDivergenceErrorCode =
  (typeof VendorRuleDivergenceErrorCode)[keyof typeof VendorRuleDivergenceErrorCode];

/** One field where the document and the rule disagree. */
export interface BillVendorRuleDivergenceItem {
  kind: typeof VendorKnowledgeKind.WITHHOLDING | typeof VendorKnowledgeKind.PIS_COFINS_CREDIT;
  /** The bill field that differs (`irrfWithholdingAmount`, `generatesPisCredit`, ...). */
  field: string;
  /** Withholding items only. */
  taxType?: VendorWithholdingTax;
  /** Amount for a withholding; flag, credit type or revenue use for a credit field. */
  documentValue: number | boolean | string | null;
  ruleValue: number | boolean | string | null;
  /** Withholding items only: the document's amount over the calculation base, in percent. */
  documentRatePercent?: number | null;
  /** Withholding items only: null when the rule says the tax is not withheld. */
  ruleRatePercent?: number | null;
}

/** The rule version a divergence was measured against. */
export interface BillVendorRuleDivergenceRule {
  kind: typeof VendorKnowledgeKind.WITHHOLDING | typeof VendorKnowledgeKind.PIS_COFINS_CREDIT;
  knowledgeId: string;
  /** `YYYY-MM` the version starts to apply. */
  effectiveFrom: string;
  serviceTypeId: string;
}

export interface BillVendorRuleDivergenceResolutionRecord {
  option: VendorRuleDivergenceResolution;
  resolvedByUserId: string;
  /** The name of the person who chose, set only when the bill is read. Never written on the bill. */
  resolvedByUserName?: string | null;
  resolvedAt: string;
  /** Option 3: the versions it created, effective from the bill's competence. */
  newVersions?: { kind: string; knowledgeId: string; effectiveFrom: string }[];
}

/**
 * What a bill records about a vendor rule divergence (`ApBill.vendorRuleDivergence`, D28 layer 2):
 * which fields differ, the document and rule values, the rule versions compared, and, once a
 * person chose, the resolution with who and when. `detectionId` changes whenever the comparison
 * changes, so a resolution always answers the divergence the person saw.
 */
export interface BillVendorRuleDivergence {
  detectionId: string;
  status: VendorRuleDivergenceStatus;
  /** `YYYY-MM` of the bill competence the rule versions were chosen for. */
  competence: string;
  rules: BillVendorRuleDivergenceRule[];
  items: BillVendorRuleDivergenceItem[];
  origin: BillVendorRuleOrigin;
  detectedAt: string;
  resolution?: BillVendorRuleDivergenceResolutionRecord;
  clearedAt?: string;
  clearedByUserId?: string | null;
  /**
   * Set only when the bill is read. True when this open divergence names a rule version that is
   * no longer the one in effect for the bill competence. Never written on the bill.
   */
  ruleChanged?: boolean;
  /** The versions in effect now, set only when the bill is read. Never written on the bill. */
  currentRules?: BillVendorRuleDivergenceRule[];
  /**
   * Set only when the bill is read, so the comparison can name a Simples/MEI optant before the
   * vendor record finishes loading. Never written on the bill.
   */
  vendorOptant?: 'SIMPLES' | 'MEI';
}

/** Tolerant reader for the `ApBill.vendorRuleDivergence` JSON column. */
export function readBillVendorRuleDivergence(value: unknown): BillVendorRuleDivergence | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.detectionId !== 'string' || record.detectionId.length === 0) return null;
  if (!Object.values(VendorRuleDivergenceStatus).includes(record.status as never)) return null;
  if (!Array.isArray(record.items) || !Array.isArray(record.rules)) return null;
  const items = record.items.filter(
    (item): item is BillVendorRuleDivergenceItem =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as Record<string, unknown>).field === 'string' &&
      ((item as Record<string, unknown>).kind === VendorKnowledgeKind.WITHHOLDING ||
        (item as Record<string, unknown>).kind === VendorKnowledgeKind.PIS_COFINS_CREDIT),
  );
  return { ...(record as unknown as BillVendorRuleDivergence), items };
}

/** True when the bill holds a divergence nobody resolved yet. */
export function hasOpenVendorRuleDivergence(value: unknown): boolean {
  const divergence = readBillVendorRuleDivergence(value);
  return divergence?.status === VendorRuleDivergenceStatus.OPEN && divergence.items.length > 0;
}

/** Body of `POST /ap/bills/:billId/vendor-rule-divergence/resolve`. */
export interface ResolveBillVendorRuleDivergenceRequest {
  option: VendorRuleDivergenceResolution;
  /** The `detectionId` the person saw; a different one is refused as stale. */
  detectionId: string;
}

// ---------------------------------------------------------------------------
// Knowledge offers on the bill (ALC-4561, D13, D14, D22, D24)
// ---------------------------------------------------------------------------

/** One rule a bill offer would save, exactly as it would be saved. */
export interface BillKnowledgeOfferRule {
  kind: typeof VendorKnowledgeKind.WITHHOLDING | typeof VendorKnowledgeKind.PIS_COFINS_CREDIT;
  /** `create`: the entity has no rule of this kind for the key yet. `version`: a new version of it. */
  action: 'create' | 'version';
  payload: VendorWithholdingPayload | VendorPisCofinsCreditPayload;
  /** The version in effect at the bill's competence, which the new version follows. */
  current: { knowledgeId: string; effectiveFrom: string; payload: Record<string, unknown> } | null;
}

/**
 * A knowledge offer on a bill. `new_service`: the entity has no withholding or credit rule for the
 * vendor's service type; the bill keeps today's rates (D14). `remember`: a person set withholdings or
 * credits by hand; saving starts the rule (or a new version of it) from the bill's competence (D22).
 * `rules` is what accepting saves; a new-service offer whose bill has nothing to propose has none,
 * and the person sets the rule on the vendor page.
 */
export interface BillKnowledgeOffer {
  /** Changes whenever what would be saved changes; accepting with another id is refused as stale. */
  offerId: string;
  offerKind: BillKnowledgeOfferKind;
  entityId: string;
  vendorId: string;
  vendorName: string | null;
  serviceTypeId: string;
  serviceType: VendorKnowledgeServiceTypeSummary | null;
  /** `YYYY-MM` of the bill: the rule starts (or its new version starts) here. */
  competence: string;
  rules: BillKnowledgeOfferRule[];
  /** Bill fields a person set by hand (remember only). */
  handSetFields: string[];
  /** A rule version already starts in the bill's competence: it is edited on the vendor page. */
  versionStartsInCompetence: boolean;
}

export interface BillKnowledgeOffersResponse {
  offers: BillKnowledgeOffer[];
}

/** Body of `POST /ap/bills/:billId/knowledge-offers/accept`. */
export interface AcceptBillKnowledgeOfferRequest {
  offerKind: BillKnowledgeOfferKind;
  offerId: string;
  /**
   * Withholding rates the person confirmed, when they edited the derived rate on the card.
   * A rate of 0 means the tax is not withheld (`applies: false`). Omitted rates stay as the
   * offer showed them.
   */
  withholdingRates?: { tax: VendorWithholdingTax; rate: number }[];
}

export interface AcceptBillKnowledgeOfferResponse {
  knowledge: VendorKnowledgeVersion[];
  serviceTypeId: string;
  /** Draft bills of the entity, vendor and service type waiting for the rule (D13). */
  waitingDrafts: number;
}

/** Body of `POST /ap/bills/:billId/knowledge-offers/this-bill-only`. */
export interface KeepBillKnowledgeOfferForThisBillRequest {
  offerId: string;
}

/** Coded refusals of the bill offer endpoints. */
export const BillKnowledgeOfferErrorCode = {
  /** The bill makes no such offer now (accepted, dismissed, or nothing left to remember). */
  NOT_AVAILABLE: 'AP_BILL_KNOWLEDGE_OFFER_NOT_AVAILABLE',
  /** What the offer would save changed since the screen loaded it. */
  STALE: 'AP_BILL_KNOWLEDGE_OFFER_STALE',
  /** A rule version already starts in the bill's competence; edit it on the vendor page. */
  VERSION_EXISTS: 'AP_BILL_KNOWLEDGE_OFFER_VERSION_EXISTS',
  /** The offer has no values to save; the rule is set on the vendor page. */
  NOTHING_TO_SAVE: 'AP_BILL_KNOWLEDGE_OFFER_NOTHING_TO_SAVE',
} as const;
export type BillKnowledgeOfferErrorCode =
  (typeof BillKnowledgeOfferErrorCode)[keyof typeof BillKnowledgeOfferErrorCode];

/** Payload type of each kind. */
export interface VendorKnowledgePayloadByKind {
  withholding: VendorWithholdingPayload;
  pis_cofins_credit: VendorPisCofinsCreditPayload;
  instruction: VendorInstructionPayload;
  reconciliation_rule: VendorReconciliationRulePayload;
}

// ---------------------------------------------------------------------------
// Rows as the API and the services return them
// ---------------------------------------------------------------------------

/**
 * The fiscal service type a row points at, as the read endpoints embed it. It travels with the
 * rows (same guard as the rows) so a reader without `settings:read` still sees a name, not an id.
 */
export interface VendorKnowledgeServiceTypeSummary {
  serviceTypeId: string;
  code: string;
  lc116Code: string | null;
  name: string;
}

/** Review status of a version (ALC-4663, D11): `VendorKnowledgeReviewStatus`, generated from Prisma. */
export interface VendorKnowledgeVersion {
  knowledgeId: string;
  entityId: string;
  vendorId: string;
  kind: VendorKnowledgeKind;
  dimensions: VendorKnowledgeDimensions;
  /** First competence this version applies to, as `YYYY-MM`. */
  effectiveFrom: string;
  /** False on a tombstone version: from `effectiveFrom` on, nothing is known for the key. */
  isActive: boolean;
  payload: Record<string, unknown>;
  createdAt: string;
  createdBy: string;
  reviewStatus: VendorKnowledgeReviewStatus;
  /** Who reviewed the version and when; null until it is reviewed. */
  reviewedBy: string | null;
  reviewedAt: string | null;
  /**
   * The service type of `dimensions.serviceTypeId`. Filled by the list read only; absent on
   * write responses and in audit snapshots, and null when the row has no service type.
   */
  serviceType?: VendorKnowledgeServiceTypeSummary | null;
}

export interface VendorKnowledgeOfferDismissal {
  dismissalId: string;
  entityId: string;
  vendorId: string;
  offerKind: VendorKnowledgeOfferKind;
  serviceTypeId: string | null;
  dismissedAt: string;
  dismissedBy: string;
  restoredAt: string | null;
  restoredBy: string | null;
  /** True while the dismissal holds (not restored). */
  active: boolean;
  /** The service type of `serviceTypeId`. Filled by the list read only. */
  serviceType?: VendorKnowledgeServiceTypeSummary | null;
}

/** A workflow whose conditions name a vendor (read-only reference for the Knowledge tab). */
export interface VendorKnowledgeWorkflowRef {
  workflowId: string;
  name: string;
  /** Workflow row status (`ACTIVE`, `INACTIVE`, ...). */
  status: string;
  /** Entity the workflow is bound to; null when it applies tenant-wide. */
  entityId: string | null;
  triggerTypeCode: string;
  triggerTypeName: string;
  /**
   * Where the rule came from (ALC-4564): the description the person wrote, or the bill it was
   * offered on. Null for a hand-made rule and for a rule made through MCP.
   */
  origin: WorkflowOriginResponse | null;
}

// ---------------------------------------------------------------------------
// Request bodies (the API DTOs implement these; the service inputs extend them)
// ---------------------------------------------------------------------------

/** Body of `POST /ap/vendor-knowledge`: the first version of a key. */
export interface CreateVendorKnowledgeRequest {
  entityId: string;
  vendorId: string;
  kind: VendorKnowledgeKind;
  /** The kind's key dimensions: `serviceTypeId` for the tax kinds, `purpose` for instruction. */
  dimensions?: VendorKnowledgeDimensions;
  /** `YYYY-MM`, first competence the version applies to. Defaults to the current month. */
  effectiveFrom?: string;
  /** Validated against the kind's payload schema. */
  payload: Record<string, unknown>;
}

/** Body of `POST /ap/vendor-knowledge/:knowledgeId/versions`. */
export interface NewVendorKnowledgeVersionRequest {
  entityId: string;
  /** `YYYY-MM`. Defaults to the current month. */
  effectiveFrom?: string;
  /** Validated against the kind's payload schema. */
  payload: Record<string, unknown>;
}

/** Body of `POST /ap/vendor-knowledge/:knowledgeId/deactivate`. */
export interface DeactivateVendorKnowledgeRequest {
  entityId: string;
  /** `YYYY-MM`, first competence the key stops applying to. Defaults to the current month. */
  effectiveFrom?: string;
}

/** Body of `POST /ap/vendor-knowledge/dismissals`. */
export interface DismissVendorKnowledgeOfferRequest {
  entityId: string;
  vendorId: string;
  /** Offer kind as registered in the catalog; an unknown one is refused with a coded error. */
  offerKind: string;
  /** Required for offers keyed by service type, refused for the others. */
  serviceTypeId?: string;
}

/** Body of `POST /ap/vendor-knowledge/dismissals/:dismissalId/restore`. */
export interface RestoreVendorKnowledgeOfferRequest {
  entityId: string;
}

/** Catalog entry as exposed over the API (the payload schema stays in code). */
export interface VendorKnowledgeCatalogKind {
  kind: VendorKnowledgeKind;
  offerKind: VendorKnowledgeOfferKind;
  appliesAt: VendorKnowledgeAppliesAt;
  keyDimensions: VendorKnowledgeDimension[];
}

// ---------------------------------------------------------------------------
// Coded errors
// ---------------------------------------------------------------------------

export const VendorKnowledgeErrorCode = {
  /** The payload does not satisfy the kind's schema. Carries `issues`. */
  VENDOR_KNOWLEDGE_INVALID_PAYLOAD: 'VENDOR_KNOWLEDGE_INVALID_PAYLOAD',
  VENDOR_KNOWLEDGE_UNKNOWN_KIND: 'VENDOR_KNOWLEDGE_UNKNOWN_KIND',
  VENDOR_KNOWLEDGE_UNKNOWN_OFFER_KIND: 'VENDOR_KNOWLEDGE_UNKNOWN_OFFER_KIND',
  /** A key dimension the kind needs is missing, or one it does not declare was sent. */
  VENDOR_KNOWLEDGE_INVALID_DIMENSIONS: 'VENDOR_KNOWLEDGE_INVALID_DIMENSIONS',
  VENDOR_KNOWLEDGE_INVALID_COMPETENCE: 'VENDOR_KNOWLEDGE_INVALID_COMPETENCE',
  VENDOR_KNOWLEDGE_VENDOR_NOT_FOUND: 'VENDOR_KNOWLEDGE_VENDOR_NOT_FOUND',
  VENDOR_KNOWLEDGE_ENTITY_NOT_FOUND: 'VENDOR_KNOWLEDGE_ENTITY_NOT_FOUND',
  VENDOR_KNOWLEDGE_SERVICE_TYPE_NOT_FOUND: 'VENDOR_KNOWLEDGE_SERVICE_TYPE_NOT_FOUND',
  VENDOR_KNOWLEDGE_NOT_FOUND: 'VENDOR_KNOWLEDGE_NOT_FOUND',
  /** Reviewing a version that is not a draft (ALC-4663). */
  VENDOR_KNOWLEDGE_NOT_DRAFT: 'VENDOR_KNOWLEDGE_NOT_DRAFT',
  VENDOR_KNOWLEDGE_DISMISSAL_NOT_FOUND: 'VENDOR_KNOWLEDGE_DISMISSAL_NOT_FOUND',
  /** `create` on a key that already has versions: use a new version instead. */
  VENDOR_KNOWLEDGE_ALREADY_EXISTS: 'VENDOR_KNOWLEDGE_ALREADY_EXISTS',
  /** A new version at a competence that already has one for the key. */
  VENDOR_KNOWLEDGE_VERSION_EXISTS: 'VENDOR_KNOWLEDGE_VERSION_EXISTS',
  /** Deactivating a key that already reads as inactive at that competence. */
  VENDOR_KNOWLEDGE_ALREADY_INACTIVE: 'VENDOR_KNOWLEDGE_ALREADY_INACTIVE',
  /** Deactivating from a competence while a later active version of the key is scheduled. */
  VENDOR_KNOWLEDGE_LATER_VERSION_EXISTS: 'VENDOR_KNOWLEDGE_LATER_VERSION_EXISTS',
  /**
   * A Brazilian tax kind (withholding of IRRF/PIS/COFINS/CSLL/INSS/ISS, PIS/COFINS credit) written
   * for an entity outside Brazil. Carries `kind` and the entity's `countryCode`.
   */
  VENDOR_KNOWLEDGE_TAX_JURISDICTION_MISMATCH: 'VENDOR_KNOWLEDGE_TAX_JURISDICTION_MISMATCH',
  /**
   * A PIS/COFINS credit rule (create or new version) starting after
   * {@link LAST_PIS_COFINS_CREDIT_COMPETENCE}. Carries `effectiveFrom` and `lastCompetence`.
   */
  VENDOR_KNOWLEDGE_CREDIT_AFTER_CUTOFF: 'VENDOR_KNOWLEDGE_CREDIT_AFTER_CUTOFF',
} as const;
export type VendorKnowledgeErrorCode =
  (typeof VendorKnowledgeErrorCode)[keyof typeof VendorKnowledgeErrorCode];

export interface VendorKnowledgeErrorMessage {
  ptBr: string;
  en: string;
}

export const VENDOR_KNOWLEDGE_ERROR_MESSAGES: Record<
  VendorKnowledgeErrorCode,
  VendorKnowledgeErrorMessage
> = {
  VENDOR_KNOWLEDGE_INVALID_PAYLOAD: {
    ptBr: 'O conteúdo informado não é válido para este tipo de conhecimento.',
    en: 'The payload is not valid for this kind of knowledge.',
  },
  VENDOR_KNOWLEDGE_UNKNOWN_KIND: {
    ptBr: 'Tipo de conhecimento desconhecido.',
    en: 'Unknown kind of knowledge.',
  },
  VENDOR_KNOWLEDGE_UNKNOWN_OFFER_KIND: {
    ptBr: 'Tipo de oferta desconhecido.',
    en: 'Unknown offer kind.',
  },
  VENDOR_KNOWLEDGE_INVALID_DIMENSIONS: {
    ptBr: 'As dimensões informadas não correspondem às que este tipo de conhecimento exige.',
    en: 'The dimensions provided do not match the ones this kind of knowledge requires.',
  },
  VENDOR_KNOWLEDGE_INVALID_COMPETENCE: {
    ptBr: 'Competência inválida. Use o formato AAAA-MM.',
    en: 'Invalid competence. Use the YYYY-MM format.',
  },
  VENDOR_KNOWLEDGE_VENDOR_NOT_FOUND: {
    ptBr: 'Fornecedor não encontrado.',
    en: 'Vendor not found.',
  },
  VENDOR_KNOWLEDGE_ENTITY_NOT_FOUND: {
    ptBr: 'Entidade não encontrada.',
    en: 'Entity not found.',
  },
  VENDOR_KNOWLEDGE_SERVICE_TYPE_NOT_FOUND: {
    ptBr: 'Tipo de serviço não encontrado ou inativo.',
    en: 'Service type not found or inactive.',
  },
  VENDOR_KNOWLEDGE_NOT_FOUND: {
    ptBr: 'Conhecimento de fornecedor não encontrado.',
    en: 'Vendor knowledge not found.',
  },
  VENDOR_KNOWLEDGE_NOT_DRAFT: {
    ptBr: 'Só um rascunho pode ser revisado.',
    en: 'Only a draft can be reviewed.',
  },
  VENDOR_KNOWLEDGE_DISMISSAL_NOT_FOUND: {
    ptBr: 'Oferta dispensada não encontrada.',
    en: 'Dismissed offer not found.',
  },
  VENDOR_KNOWLEDGE_ALREADY_EXISTS: {
    ptBr: 'Já existe conhecimento para esta chave. Inicie uma nova versão.',
    en: 'Knowledge already exists for this key. Start a new version instead.',
  },
  VENDOR_KNOWLEDGE_VERSION_EXISTS: {
    ptBr: 'Já existe uma versão desta chave para a competência informada.',
    en: 'A version of this key already exists for the competence provided.',
  },
  VENDOR_KNOWLEDGE_ALREADY_INACTIVE: {
    ptBr: 'Este conhecimento já está inativo na competência informada.',
    en: 'This knowledge is already inactive at the competence provided.',
  },
  VENDOR_KNOWLEDGE_LATER_VERSION_EXISTS: {
    ptBr: 'Há uma versão posterior ativa desta chave. Desative a partir de uma competência posterior a ela.',
    en: 'A later active version of this key exists. Deactivate from a competence after it.',
  },
  VENDOR_KNOWLEDGE_TAX_JURISDICTION_MISMATCH: {
    ptBr: 'Regras de impostos brasileiros só valem para entidades no Brasil.',
    en: 'Brazilian tax rules only apply to entities in Brazil.',
  },
  VENDOR_KNOWLEDGE_CREDIT_AFTER_CUTOFF: {
    ptBr: 'Créditos de PIS/COFINS terminam na competência 12/2026. Escolha uma competência até 12/2026 ou marque "Sem regra" no crédito.',
    en: 'PIS/COFINS credits end with the 12/2026 competence. Pick a competence up to 12/2026 or choose "No rule" for the credit.',
  },
};
